import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import { caseStudiesData } from '../data/caseStudiesData';
import styles from './CaseStudiesPage.module.css';

const CaseStudiesPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get unique categories
  const categories = ['All', ...new Set(caseStudiesData.map(cs => cs.category))];

  // Filter case studies
  const filteredCaseStudies = selectedCategory === 'All' 
    ? caseStudiesData 
    : caseStudiesData.filter(cs => cs.category === selectedCategory);

  return (
    <div className={styles.caseStudiesPage}>
      <SEO 
        title="Case Studies | Real Results from SolveDot Labs Software Solutions"
        description="Explore real case studies from SolveDot Labs: AI chatbot transformation, IoT smart home systems, custom CRM solutions. See how we've helped businesses achieve 40% reduction in response times, 100% uptime, and remarkable results."
        keywords="SolveDot Labs Case Studies, Software Development Case Studies, AI Solutions Case Studies, IoT Case Studies, CRM Case Studies, Software Success Stories"
      />
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      {/* Enhanced Hero Header */}
      <section className={styles.heroHeader}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOrb1}></div>
          <div className={styles.heroOrb2}></div>
        </div>
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>Case Studies</span>
          <h1 className={styles.heroTitle}>
            Real Results.
            <br />
            <span className={styles.titleHighlight}>Real Impact.</span>
          </h1>
          <p className={styles.heroDescription}>
            Discover how we've helped businesses transform their operations, solve complex challenges, and achieve remarkable results through innovative technology solutions.
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className={styles.filtersSection}>
        <div className={styles.filtersContainer}>
          <div className={styles.categoryFilters}>
            <span className={styles.filterLabel}>Filter by Category:</span>
            {categories.map(category => (
              <button
                key={category}
                className={`${styles.filterButton} ${selectedCategory === category ? styles.activeFilter : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.resultsInfo}>
          <span className={styles.resultsCount}>
            {filteredCaseStudies.length} {filteredCaseStudies.length === 1 ? 'Case Study' : 'Case Studies'} Found
          </span>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className={styles.caseStudiesSection}>
        <div className={styles.caseStudiesGrid}>
          {filteredCaseStudies.map((caseStudy, index) => (
            <div 
              key={caseStudy.id} 
              className={styles.caseStudyCard}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={styles.cardHeader}>
                <div className={styles.iconWrapper}>
                  <div className={styles.iconGlow}></div>
                  <span className={styles.icon}>{caseStudy.icon}</span>
                </div>
                <div className={styles.cardMeta}>
                  <span className={styles.categoryBadge}>{caseStudy.category}</span>
                  <span className={styles.industryBadge}>{caseStudy.industry}</span>
                </div>
              </div>
              
              <div className={styles.cardContent}>
                <h3 className={styles.caseStudyTitle}>{caseStudy.title}</h3>
                <div className={styles.clientInfo}>
                  <span className={styles.clientLabel}>Client:</span>
                  <span className={styles.clientName}>{caseStudy.client}</span>
                </div>

                <div className={styles.challengeSection}>
                  <h4 className={styles.sectionLabel}>Challenge</h4>
                  <p className={styles.sectionText}>{caseStudy.challenge}</p>
                </div>

                <div className={styles.solutionSection}>
                  <h4 className={styles.sectionLabel}>Solution</h4>
                  <p className={styles.sectionText}>{caseStudy.solution}</p>
                </div>

                <div className={styles.resultsSection}>
                  <h4 className={styles.sectionLabel}>Results</h4>
                  <ul className={styles.resultsList}>
                    {caseStudy.results.map((result, idx) => (
                      <li key={idx} className={styles.resultItem}>
                        <span className={styles.checkIcon}>✓</span>
                        <span>{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles.technologiesSection}>
                  <h4 className={styles.techLabel}>Technologies Used</h4>
                  <div className={styles.techTags}>
                    {caseStudy.technologies.map((tech, idx) => (
                      <span key={idx} className={styles.techTag}>{tech}</span>
                    ))}
                  </div>
                </div>

                {caseStudy.testimonial && (
                  <div className={styles.testimonialBox}>
                    <div className={styles.quoteIcon}>"</div>
                    <p className={styles.testimonialQuote}>{caseStudy.testimonial.quote}</p>
                    <div className={styles.testimonialAuthor}>
                      <span className={styles.authorName}>— {caseStudy.testimonial.author}</span>
                      <span className={styles.authorCompany}>{caseStudy.testimonial.company}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to Write Your Success Story?</h2>
          <p className={styles.ctaDescription}>
            Let's discuss how we can help you achieve similar results. Every project is an opportunity to create something extraordinary.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/contact" className={styles.primaryCta}>
              <span>Start Your Project</span>
              <span className={styles.arrow}>→</span>
            </Link>
            <Link to="/services" className={styles.secondaryCta}>
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;

